import { useCallback, useEffect, useRef } from "react";
import { useRailItems } from "@/hooks/agent/useRailItems";
import { useSurfacedResource } from "@/hooks/agent/useSurfacedResource";
import { trackEvent, trackEventOnce } from "@/lib/ga4";
import { activityService } from "@/services/analytics/ActivityService";
import { AgentPageProps } from "@/types/agent/agent-page";
import { InfoLinksConfig } from "@/types/agent/info-links";
import type { RailItem } from "@/types/voice/resources-rail";

interface TranscriptMessageLike {
  message: string;
  isUser: boolean;
}

/**
 * QR-scan activity logging (?ref=qr, deferred across the invite gate so a
 * consumerId can be attached) plus the resources-rail item list, "just
 * named this" surfaced-resource highlight, and its GA tracking handlers.
 * Grouped together because both are "agent resolved → log/derive something"
 * concerns that don't touch conversation or panel state.
 */
export function useQrActivityLogging(
  agent: AgentPageProps["agentPageData"] | null,
  showInviteGate: boolean,
  messages: TranscriptMessageLike[],
  resourcesConfig: InfoLinksConfig | null,
) {
  const pendingQrLogRef = useRef<{ genieId: string; genieName: string; urlName: string } | null>(null);
  const qrLoggedRef = useRef(false);

  useEffect(() => {
    if (!agent) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("ref") !== "qr") return;
    const genieId = agent.id;
    const genieName = agent.name ?? "";
    if (!genieId) return;
    if (showInviteGate) {
      pendingQrLogRef.current = { genieId, genieName, urlName: agent.url_name ?? "" };
      return;
    }
    if (qrLoggedRef.current) return;
    qrLoggedRef.current = true;
    activityService.logQrScanned(genieId, genieName, agent.url_name ?? "");
  }, [agent?.id, showInviteGate]);

  const flushPendingQrLog = useCallback((consumerId?: string | null) => {
    if (!pendingQrLogRef.current || qrLoggedRef.current) return;
    qrLoggedRef.current = true;
    const { genieId, genieName, urlName: qrUrlName } = pendingQrLogRef.current;
    pendingQrLogRef.current = null;
    activityService.logQrScanned(genieId, genieName, qrUrlName, consumerId);
  }, []);

  /** Clears the once-per-mount QR-log guards — call when the page moves to a
   *  different genie without remounting, so a fresh QR scan can log again. */
  const resetQrLog = useCallback(() => {
    qrLoggedRef.current = false;
    pendingQrLogRef.current = null;
  }, []);

  const railItems = useRailItems(resourcesConfig);
  const surfacedResourceId = useSurfacedResource(messages, railItems);

  const handleRailSelect = useCallback(
    (item: RailItem) => {
      trackEvent("resource_opened", {
        resource_id: item.id,
        resource_kind: item.kind,
        resource_label: item.label,
        surface: "rail",
        agent_id: agent?.id ?? undefined,
      });
    },
    [agent?.id],
  );

  const handleRailImpression = useCallback(
    (item: RailItem) => {
      trackEventOnce(`rail_impression_${item.id}`, "resource_impression", {
        resource_id: item.id,
        resource_kind: item.kind,
        surface: "rail",
        agent_id: agent?.id ?? undefined,
      });
    },
    [agent?.id],
  );

  return {
    flushPendingQrLog,
    resetQrLog,
    railItems,
    surfacedResourceId,
    handleRailSelect,
    handleRailImpression,
  };
}
